/**
 * 🔄 DEVICE SYNC PROGRESS INDICATOR
 * 
 * Shows persistent sync progress for Garmin device connections.
 * Similar to PipelineProgressIndicator but for device data sync.
 * 
 * Features:
 * - Real-time polling of sync progress
 * - Persistent at bottom of chat (doesn't scroll away)
 * - Shows sync stages with progress
 * - Auto-hides when sync completes
 * 
 * Author: Systems Architect
 * Date: January 2025
 */

import React, { useState, useEffect } from 'react';
import { Check, Loader2, Circle, AlertCircle, Activity } from 'lucide-react';
import { getApiBaseUrl } from '../utils/api';
import './DeviceSyncProgressIndicator.css';
import { CoachCedarColors } from '../utils/styleConverter';

interface SyncStage {
  stage: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at?: string | null;
  completed_at?: string | null;
  message?: string;
}

interface SyncProgress {
  sync_id: string | null;
  status: string;
  total_activities: number | null;
  processed_activities: number | null;
  cards_created: number | null;
  current_activity: string | null;
  messages: Array<{ text: string; timestamp: string; status: string }>;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  is_active: boolean;
}

type SyncServiceType = 'garmin' | 'apple_health' | 'health_connect' | 'strava';

interface DeviceSyncProgressIndicatorProps {
  service: SyncServiceType;
  onComplete?: () => void;
}

export const DeviceSyncProgressIndicator: React.FC<DeviceSyncProgressIndicatorProps> = ({
  service,
  onComplete
}) => {
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [richSummary, setRichSummary] = useState<string | null>(null);

  // Service display names & icons (dynamic based on service prop)
  const SERVICE_DISPLAY: Record<SyncServiceType, string> = {
    garmin: 'Garmin',
    apple_health: 'Apple Health',
    health_connect: 'Health Connect',
    strava: 'Strava',
  };
  const serviceName = SERVICE_DISPLAY[service] || service;
  const serviceIcon = <Activity className="w-5 h-5" />;

  // 🚀 REAL-TIME POLLING: Poll backend every 3 seconds for actual sync status
  useEffect(() => {
    let pollInterval: NodeJS.Timeout;
    let isActive = true;
    
    const fetchSyncStatus = async () => {
      try {
        const { storage } = await import('../services/storage');
        const jwtToken = await storage.getItem('jwt_token');
        
        if (!jwtToken) {
          setError('Authentication required');
          return;
        }
        
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/native/sync-progress/${service}`, {
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            // Sync hasn't started yet, retry
            setPollCount(prev => prev + 1);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        
        const progress: SyncProgress = await response.json();
        setSyncProgress(progress);
        setPollCount(prev => prev + 1);
        
        // Check if sync is complete
        if (progress.status === 'completed' && isActive) {
          console.log('🔄 DEVICE SYNC COMPLETE: Fetching rich post-sync summary');
          // Stop polling immediately
          if (pollInterval) {
            clearInterval(pollInterval);
          }

          // Fetch the rich post-sync summary from backend
          try {
            const summaryResponse = await fetch(
              `${apiBaseUrl}/api/native/post-sync-summary/${service}`,
              {
                headers: {
                  'Authorization': `Bearer ${jwtToken}`,
                  'Content-Type': 'application/json',
                },
              }
            );
            if (summaryResponse.ok) {
              const summaryData = await summaryResponse.json();
              if (summaryData.summary) {
                setRichSummary(summaryData.summary);
                console.log(
                  `✅ DEVICE SYNC: Rich summary received (${summaryData.activities_synced} activities, ${summaryData.cards_created} cards)`
                );
              }
            }
          } catch (summaryErr) {
            console.warn('⚠️ DEVICE SYNC: Could not fetch post-sync summary', summaryErr);
            // Non-critical — the progress indicator still shows "completed"
          }

          // Call onComplete after a short delay so user can read the summary
          if (onComplete) {
            setTimeout(() => onComplete(), 5000); // 5s so user can read the summary
          }
        } else if (progress.status === 'failed') {
          setError(progress.error || 'Sync failed - please try again');
          if (pollInterval) {
            clearInterval(pollInterval);
          }
        }
        
      } catch (err) {
        console.error('❌ Error fetching sync status:', err);
        setError('Failed to fetch status');
        setPollCount(prev => prev + 1);
        
        // Don't stop polling on transient errors - retry
        // Only stop after 40 failed attempts (40 * 3s = 2 minutes of failures)
        if (pollCount > 40) {
          if (pollInterval) {
            clearInterval(pollInterval);
          }
        }
      }
    };
    
    // Initial fetch
    fetchSyncStatus();
    
    // Poll every 3 seconds
    pollInterval = setInterval(fetchSyncStatus, 3000);
    
    return () => {
      isActive = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [service, onComplete, pollCount]);

  // Build sync stages from progress
  const getSyncStages = (): SyncStage[] => {
    if (!syncProgress) return [];

    const stages: SyncStage[] = [];
    const status = syncProgress.status;
    
    // 🔧 FIX: Defensive access - messages array may be undefined when backend
    // returns minimal response (e.g., {"status": "no_active_sync", "is_active": false})
    // Same pattern as GoalPipelineProgress which uses optional chaining on phases
    const messages = syncProgress.messages || [];

    // Stage 1: Connecting
    stages.push({
      stage: 'connecting',
      title: `Connecting to ${serviceName}...`,
      status: status === 'connecting' ? 'in_progress' : 
              ['fetching', 'processing', 'creating_cards', 'completed'].includes(status) ? 'completed' : 'pending',
      message: messages.find(m => m.status === 'connecting')?.text
    });

    // Stage 2: Triggering Backfill (Garmin only)
    if (service === 'garmin') {
      stages.push({
        stage: 'triggering_backfill',
        title: 'Requesting your full data history...',
        status: status === 'fetching' && syncProgress.processed_activities === 0 ? 'in_progress' :
                ['processing', 'creating_cards', 'completed'].includes(status) ? 'completed' : 'pending',
        message: 'Activities, health metrics, and women\'s health data will arrive over the next 5-10 minutes'
      });
    }

    // Stage 3: Fetching Activities
    stages.push({
      stage: 'fetching',
      title: syncProgress.total_activities 
        ? `Found ${syncProgress.total_activities} activities...`
        : 'Fetching activities...',
      status: status === 'fetching' ? 'in_progress' :
              ['processing', 'creating_cards', 'completed'].includes(status) ? 'completed' : 'pending',
      message: messages.find(m => m.status === 'fetching')?.text
    });

    // Stage 4: Processing Activities
    if (syncProgress.total_activities && syncProgress.total_activities > 0) {
      stages.push({
        stage: 'processing',
        title: `Processing activities... (${syncProgress.processed_activities || 0} of ${syncProgress.total_activities})`,
        status: status === 'processing' ? 'in_progress' :
                ['creating_cards', 'completed'].includes(status) ? 'completed' : 'pending',
        message: syncProgress.current_activity 
          ? `Current: ${syncProgress.current_activity.slice(0, 40)}${syncProgress.current_activity.length > 40 ? '...' : ''}`
          : undefined
      });
    }

    // Stage 5: Creating Cards
    stages.push({
      stage: 'creating_cards',
      title: syncProgress.cards_created 
        ? `Creating workout cards... (${syncProgress.cards_created} created)`
        : 'Creating workout cards...',
      status: status === 'creating_cards' ? 'in_progress' :
              status === 'completed' ? 'completed' : 'pending',
      message: messages.find(m => m.status === 'creating_cards')?.text
    });

    return stages;
  };

  const getStageIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-5 h-5" />;
      case 'in_progress':
        return <Loader2 className="w-5 h-5 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Circle className="w-5 h-5" />;
    }
  };

  // Calculate elapsed time
  const getElapsedTime = (): number | null => {
    if (!syncProgress || !syncProgress.started_at) return null;
    const startTime = new Date(syncProgress.started_at).getTime();
    const now = Date.now();
    return Math.floor((now - startTime) / 1000);
  };

  // Get estimated time remaining
  const getEstimatedTimeRemaining = (): string => {
    if (!syncProgress) return '~30-60 seconds';
    
    const elapsed = getElapsedTime() || 0;
    const total = syncProgress.total_activities || 0;
    const processed = syncProgress.processed_activities || 0;
    
    if (total === 0 || processed === 0) {
      return '~30-60 seconds';
    }
    
    // Estimate: ~2-3 seconds per activity
    const avgTimePerActivity = 2.5;
    const remaining = (total - processed) * avgTimePerActivity;
    
    if (remaining < 30) {
      return 'Almost done!';
    }
    
    const seconds = Math.ceil(remaining);
    if (seconds < 60) {
      return `~${seconds} seconds remaining`;
    }
    
    const minutes = Math.ceil(seconds / 60);
    return `~${minutes} min remaining`;
  };

  // Show loading state until first poll returns meaningful data
  // 🔧 FIX: Also treat "no_active_sync" as loading state - the backend sync
  // may not have started yet (background task takes a few seconds to register)
  if ((!syncProgress || syncProgress.status === 'no_active_sync') && !error) {
    return (
      <div className="device-sync-loading">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin device-sync-stage-icon in_progress" />
          <p className="device-sync-stage-message">Initializing sync...</p>
        </div>
      </div>
    );
  }
  
  // Show error state if polling failed
  if (error) {
    return (
      <div className="device-sync-error">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 device-sync-stage-icon failed" />
          <p className="device-sync-error-text">{error}</p>
        </div>
      </div>
    );
  }

  const stages = getSyncStages();
  const elapsed = getElapsedTime();
  const isCompleted = syncProgress?.status === 'completed';

  // ─── Rich Summary View (replaces stage-by-stage when complete) ─────
  if (isCompleted && richSummary) {
    return (
      <div className="device-sync-progress-indicator device-sync-completed">
        <div className="device-sync-header">
          <h3 className="device-sync-title">
            <Check className="device-sync-icon" style={{ color: CoachCedarColors.cedarPrime }} />
            {serviceName} Synced
          </h3>
          <span className="device-sync-status-badge completed">Complete</span>
        </div>

        <div className="device-sync-summary">
          <p className="device-sync-summary-text">{richSummary}</p>
        </div>

        <div className="device-sync-footer">
          <p className="device-sync-footer-secondary">
            Continue chatting — Cedar now has your data.
          </p>
        </div>
      </div>
    );
  }

  // ─── In-Progress View (stage-by-stage progress) ────────────────────
  return (
    <div className="device-sync-progress-indicator">
      {/* Header */}
      <div className="device-sync-header">
        <h3 className="device-sync-title">
          {serviceIcon}
          Syncing {serviceName}
        </h3>
        
        {/* Status Badge */}
        {syncProgress && (
          <div className="device-sync-status-container">
            <span className={`device-sync-status-badge ${syncProgress.status}`}>
              {isCompleted ? 'Complete' :
               syncProgress.status === 'failed' ? 'Failed' :
               'In Progress'}
            </span>
            {syncProgress.is_active && (
              <span className="device-sync-time-info">
                {getEstimatedTimeRemaining()}
              </span>
            )}
            {elapsed != null && (
              <span className="device-sync-time-info" style={{ color: '#64748B' }}>
                {Math.floor(elapsed / 60)}m {elapsed % 60}s
              </span>
            )}
          </div>
        )}
      </div>

      {/* Progress Stages */}
      <div className="device-sync-stages">
        {stages.map((stage) => (
          <div key={stage.stage} className="device-sync-stage-row">
            {/* Icon */}
            <div className={`device-sync-stage-icon ${stage.status}`}>
              {getStageIcon(stage.status)}
            </div>

            {/* Stage Content */}
            <div className="device-sync-stage-content">
              <p className={`device-sync-stage-title ${stage.status}`}>
                {stage.title}
              </p>
              
              {/* Stage message */}
              {stage.message && (
                <p className="device-sync-stage-message">
                  {stage.message}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="device-sync-footer">
        <p className="device-sync-footer-primary">
          Continue chatting while your data syncs in the background.
        </p>
        <p className="device-sync-footer-secondary">
          Auto-updating • Poll #{pollCount}
        </p>
      </div>
    </div>
  );
};

